import { Button } from "@octopusdeploy/design-system-components";
import { useInlineStatusMutation } from "@octopusdeploy/octopus-react-client";
import type { GetEphemeralEnvironmentsOnboardingDataBffResponse, ConfigureEphemeralEnvironmentsBffCommand, ProjectResource, RunbookSelection } from "@octopusdeploy/octopus-server-client";
import { useTrackEvent, useAnonymizeEventProperty } from "@octopusdeploy/portal-analytics";
import { useEffect, useState } from "react";
import type { PagedOnboardingDialogPageData } from "~/components/OnboardingDialog/PagedOnboardingDialog";
import { PagedOnboardingDialog } from "~/components/OnboardingDialog/PagedOnboardingDialog";
import { AutomaticDeploymentsContent, AutoDeploymentHelp, automaticDeploymentHelpImage, defaultBranchNameTemplate } from "./ConfigureAutomaticDeploymentContent";
import { BranchHelp, BranchContent, branchHelpImage } from "./ConfigureBranchContent";
import { ParentEnvironmentContent, ParentEnvironmentHelp, parentEnvironmentHelpImage } from "./ConfigureParentEnvironmentContent";
import { RunbookContent, DeprovisioningRunbookHelp, DeprovisioningRunbookHelpImage, ProvisioningRunbookHelp, ProvisioningRunbookHelpImage } from "./ConfigureRunbookContent";
import { ErrorContent } from "./ErrorContent";
import { ReviewContent } from "./ReviewContent";
import { SuccessContent, SuccessHelp, successHelpImage } from "./SuccessContent";
type EphemeralEnvironmentsConfigWizardProps = {
    isOpen: boolean;
    closeDialog: () => void;
    onboardingData?: GetEphemeralEnvironmentsOnboardingDataBffResponse;
    project: ProjectResource;
};
export const ephemeralEnvironmentConfigurationStart = "Ephemeral Environment Configuration: Start";
export const ephemeralEnvironmentConfigurationSuccess = "Ephemeral Environment Configuration: Success";
export const ephemeralEnvironmentConfigurationError = "Ephemeral Environment Configuration: Error";
export const ephemeralEnvironmentConfigurationCancelled = "Ephemeral Environment Configuration: Cancelled";
export type ParentEnvironmentInputMode = "Select" | "Create";
export type ParentEnvironmentConfig = {
    id?: string;
    name?: string;
    action: ParentEnvironmentInputMode;
};
export type RunbooksInputMode = "Select" | "Skip" | "Create";
export type RunbookConfig = {
    id?: string;
    name?: string;
    action: RunbooksInputMode;
};
export type BranchInputMode = "Default" | "Create";
export type BranchConfig = {
    branch?: string;
    action: BranchInputMode;
};
export type EphemeralEnvironmentsConfigurationStatus = "Not Started" | "Pending" | "Success" | "Error";
export function EphemeralEnvironmentsConfigWizard({ isOpen, closeDialog, onboardingData, project }: EphemeralEnvironmentsConfigWizardProps) {
    if (!onboardingData) {
        throw new Error("Missing onboarding data required for ephemeral environments configuration.");
    }
    const trackEvent = useTrackEvent();
    const anonymize = useAnonymizeEventProperty();
    const [configurationStatus, setConfigurationStatus] = useState<EphemeralEnvironmentsConfigurationStatus>("Not Started");
    const [configurationError, setConfigurationError] = useState<Error | undefined>(undefined);
    const [currentPageName, setCurrentPageName] = useState<string>("Parent environment");
    const defaultRunbookMode = getDefaultRunbookMode(onboardingData);
    // State to hold the configuration for each step of the wizard
    const [parentEnvironmentConfig, setParentEnvironmentConfig] = useState<ParentEnvironmentConfig>({ id: undefined, name: undefined, action: "Select" });
    const [nameTemplateConfig, setNameTemplateConfig] = useState<string>(project.IsVersionControlled ? defaultBranchNameTemplate : "");
    const [automaticEphemeralEnvironmentDeployments, setAutomaticEphemeralEnvironmentDeployments] = useState<boolean>(true);
    const [useAutomaticBranchName, setUseAutomaticBranchName] = useState<boolean>(true);
    const [provisioningConfig, setProvisioningConfig] = useState<RunbookConfig>({ id: undefined, name: undefined, action: defaultRunbookMode });
    const [deprovisioningConfig, setDeprovisioningConfig] = useState<RunbookConfig>({ id: undefined, name: undefined, action: defaultRunbookMode });
    const [branchConfig, setBranchConfig] = useState<BranchConfig | undefined>(undefined);
    const [channelName, setChannelName] = useState<string | undefined>(undefined);
    const [isCommittingRunbooksToNewBranch, setIsCommittingRunbooksToNewBranch] = useState(false);
    useEffect(() => {
        const isCommitting = onboardingData.Project.GitRunbooks && (provisioningConfig.action === "Create" || deprovisioningConfig.action === "Create");
        setIsCommittingRunbooksToNewBranch(isCommitting);
        if (isCommitting) {
            setBranchConfig({ branch: undefined, action: "Create" });
        }
        // `onboardingData` values won't change unless the entire page reloads.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [provisioningConfig.action, deprovisioningConfig.action]);
    const defaultBranch = onboardingData.Project.GitRunbooks ? onboardingData.Project.DefaultBranch : undefined;
    const isDefaultBranchProtected = onboardingData.Project.GitRunbooks ? onboardingData.Project.DefaultBranchIsProtected : undefined;
    const title = "Configure ephemeral environments";
    const parentEnvironmentConfigIsValid = parentEnvironmentConfig.id || (parentEnvironmentConfig.name && parentEnvironmentConfig.name.trim().length > 0);
    const nameTemplateConfigIsValid = nameTemplateConfig.trim().length > 0;
    const provisioningConfigIsValid = provisioningConfig.id || provisioningConfig.name || provisioningConfig.action === "Skip";
    const deprovisioningConfigIsValid = deprovisioningConfig.id || deprovisioningConfig.name || deprovisioningConfig.action === "Skip";
    const branchConfigIsValid = !!branchConfig?.branch;
    const reviewAndConfirmPageData: PagedOnboardingDialogPageData = {
        name: "Review and confirm",
        title: "Review ephemeral environments configuration",
        render: () => (<ReviewContent parentEnvironmentConfig={parentEnvironmentConfig} nameTemplateConfig={nameTemplateConfig} provisioningConfig={provisioningConfig} deprovisioningConfig={deprovisioningConfig} automaticEphemeralEnvironmentDeployments={automaticEphemeralEnvironmentDeployments ?? true} //this has to be set to reach this point. This is just appeasing the type checker
         branch={branchConfig ? branchConfig.branch : undefined}/>),
        alwaysShowCancelButton: configurationStatus != "Pending",
        hideCancelButton: configurationStatus == "Pending",
        customPrimaryButton: <Button label="Confirm" importance="primary" onClick={() => saveConfiguration()} disabled={configurationStatus == "Pending"}/>,
        isBusy: configurationStatus == "Pending",
        helpPanelHidden: true,
        onPageDisplayed: async () => {
            setCurrentPageName("Review and confirm");
        },
    };
    const successPageData: PagedOnboardingDialogPageData = {
        name: "Success",
        title: "Ephemeral environments are configured",
        helpPanelContent: <SuccessHelp />,
        helpPanelImage: successHelpImage,
        render: () => (<SuccessContent isSuccessStatus parentEnvironmentConfig={parentEnvironmentConfig} nameTemplateConfig={nameTemplateConfig} provisioningConfig={provisioningConfig} deprovisioningConfig={deprovisioningConfig} branch={branchConfig ? branchConfig.branch : undefined} automaticEphemeralEnvironmentDeployments={automaticEphemeralEnvironmentDeployments ?? true} //this has to be set to reach this point. This is just appeasing the type checker
         channelName={channelName}/>),
        onlyShowCloseButton: true,
        hideCancelButton: true,
        customPrimaryButton: (<Button label="Got it" importance="primary" onClick={() => {
                closeDialog();
            }}/>),
        helpPanelHeight: 600 as const,
    };
    const errorPageData: PagedOnboardingDialogPageData = {
        name: "Error",
        title: title,
        render: () => <ErrorContent error={configurationError}/>,
        customPrimaryButton: (<Button label="Try again" importance="primary" onClick={() => {
                setConfigurationStatus("Not Started");
            }}/>),
        helpPanelHidden: true,
        onPageDisplayed: async () => {
            setCurrentPageName("Error");
        },
    };
    const branchPageData: PagedOnboardingDialogPageData = {
        name: "Branch",
        title: title,
        helpPanelContent: <BranchHelp defaultBranchName={defaultBranch}/>,
        helpPanelImage: branchHelpImage,
        render: () => <BranchContent defaultBranchName={defaultBranch} isDefaultBranchProtected={isDefaultBranchProtected} branchConfig={branchConfig} setBranchConfig={setBranchConfig}/>,
        alwaysShowCancelButton: true,
        disableNextButton: !branchConfigIsValid,
        nextButtonText: "Review",
        helpPanelHeight: 600,
        onPageDisplayed: async () => {
            setCurrentPageName("Branch");
        },
    };
    const pages: PagedOnboardingDialogPageData[] = [
        {
            name: "Parent environment",
            title: title,
            helpPanelContent: <ParentEnvironmentHelp />,
            helpPanelImage: parentEnvironmentHelpImage,
            render: () => <ParentEnvironmentContent data={onboardingData} parentEnvironmentConfig={parentEnvironmentConfig} setParentEnvironmentConfig={setParentEnvironmentConfig} isCommittingRunbooksToNewBranch={isCommittingRunbooksToNewBranch}/>,
            alwaysShowCancelButton: true,
            disableNextButton: !parentEnvironmentConfigIsValid,
            helpPanelHeight: 600,
            onPageDisplayed: async () => {
                setCurrentPageName("Parent environment");
            },
        },
        ...[
            {
                name: "Auto Deploy",
                title: title,
                helpPanelContent: <AutoDeploymentHelp />,
                helpPanelImage: automaticDeploymentHelpImage,
                render: () => (<AutomaticDeploymentsContent isGitProject={project.IsVersionControlled} nameTemplateConfig={nameTemplateConfig} setNameTemplateConfig={setNameTemplateConfig} isCommittingRunbooksToNewBranch={isCommittingRunbooksToNewBranch} useAutomaticBranchName={useAutomaticBranchName} setUseAutomaticBranchName={setUseAutomaticBranchName} automaticEphemeralEnvironmentDeployments={automaticEphemeralEnvironmentDeployments} setAutomaticEphemeralEnvironmentDeployments={setAutomaticEphemeralEnvironmentDeployments}/>),
                alwaysShowCancelButton: true,
                disableNextButton: automaticEphemeralEnvironmentDeployments === true && !nameTemplateConfigIsValid,
                helpPanelHeight: 600 as const,
                onPageDisplayed: async () => {
                    setCurrentPageName("Auto deploy");
                },
            },
        ],
        {
            name: "Provisioning runbook",
            title: title,
            helpPanelContent: <ProvisioningRunbookHelp />,
            helpPanelImage: ProvisioningRunbookHelpImage,
            render: () => <RunbookContent data={onboardingData} runbookType="Provisioning" runbookConfig={provisioningConfig} setRunbookConfig={setProvisioningConfig} isCommittingRunbooksToNewBranch={isCommittingRunbooksToNewBranch}/>,
            alwaysShowCancelButton: true,
            disableNextButton: !provisioningConfigIsValid,
            helpPanelHeight: 600,
            onPageDisplayed: async () => {
                setCurrentPageName("Provisioning runbook");
            },
        },
        {
            name: "Deprovisioning runbook",
            title: title,
            helpPanelContent: <DeprovisioningRunbookHelp />,
            helpPanelImage: DeprovisioningRunbookHelpImage,
            render: () => <RunbookContent data={onboardingData} runbookType="Deprovisioning" runbookConfig={deprovisioningConfig} setRunbookConfig={setDeprovisioningConfig} isCommittingRunbooksToNewBranch={isCommittingRunbooksToNewBranch}/>,
            alwaysShowCancelButton: true,
            disableNextButton: !deprovisioningConfigIsValid,
            nextButtonText: !isCommittingRunbooksToNewBranch ? "Review" : undefined, // Fall back to the default if this is not the last configuration page
            helpPanelHeight: 600,
            onPageDisplayed: async () => {
                setCurrentPageName("Deprovisioning runbook");
            },
        },
        ...(isCommittingRunbooksToNewBranch ? [branchPageData] : []),
        ...(configurationStatus === "Not Started" || configurationStatus == "Pending" ? [reviewAndConfirmPageData] : []),
        ...(configurationStatus === "Success" ? [successPageData] : []),
        ...(configurationStatus === "Error" ? [errorPageData] : []),
    ];
    const { execute: submitConfiguration } = useInlineStatusMutation({
        name: "Submit ephemeral environments configuration",
        action: async (repository, provisioningRunbook?: RunbookSelection, deprovisioningRunbook?: RunbookSelection) => {
            const configuration: ConfigureEphemeralEnvironmentsBffCommand = {
                SpaceId: project.SpaceId,
                ProjectId: project.Id,
                EphemeralEnvironmentNameTemplate: nameTemplateConfig,
                ParentEnvironment: parentEnvironmentConfig.id ? { ExistingId: parentEnvironmentConfig.id } : { NewName: parentEnvironmentConfig.name },
                ProvisioningRunbook: provisioningRunbook,
                DeprovisioningRunbook: deprovisioningRunbook,
                RunbooksBranch: branchConfig ? branchConfig.branch : undefined,
                AutomaticEphemeralEnvironmentDeployments: automaticEphemeralEnvironmentDeployments,
            };
            return await repository.EphemeralEnvironments.configureEphemeralEnvironments(project.SpaceId, project.Id, configuration);
        },
        afterComplete: async (_, result) => {
            setChannelName(result.ChannelName);
            setConfigurationStatus("Success");
            trackEvent({
                eventName: ephemeralEnvironmentConfigurationSuccess,
                "Space Id": anonymize(project.SpaceId),
                "Project Id": anonymize(project.Id),
            });
        },
        onError: (error) => {
            setConfigurationError(error);
            setConfigurationStatus("Error");
            trackEvent({
                eventName: ephemeralEnvironmentConfigurationError,
                "Space Id": anonymize(project.SpaceId),
                "Project Id": anonymize(project.Id),
            });
        },
    });
    function saveConfiguration() {
        setConfigurationStatus("Pending");
        let provisioningRunbook: RunbookSelection | undefined = undefined;
        let deprovisioningRunbook: RunbookSelection | undefined = undefined;
        try {
            if (!parentEnvironmentConfig) {
                throw new Error("Parent environment configuration is required.");
            }
            if (!nameTemplateConfig && automaticEphemeralEnvironmentDeployments) {
                throw new Error("Name template configuration is required.");
            }
            if (provisioningConfig.action === "Select") {
                if (!provisioningConfig.id) {
                    throw new Error("Please select a provisioning runbook or choose the option to 'skip'.");
                }
                provisioningRunbook = { ExistingId: provisioningConfig.id };
            }
            if (provisioningConfig.action === "Create") {
                if (!provisioningConfig.name) {
                    throw new Error("To create a provisioning runbook please provide a name for the new runbook.");
                }
                provisioningRunbook = { NewName: provisioningConfig.name };
            }
            if (deprovisioningConfig.action === "Select") {
                if (!deprovisioningConfig.id) {
                    throw new Error("Please select a deprovisioning runbook or choose the option to 'skip'.");
                }
                deprovisioningRunbook = { ExistingId: deprovisioningConfig.id };
            }
            if (deprovisioningConfig.action === "Create") {
                if (!deprovisioningConfig.name) {
                    throw new Error("To create a deprovisioning runbook please provide a name for the new runbook.");
                }
                deprovisioningRunbook = { NewName: deprovisioningConfig.name };
            }
        }
        catch (error) {
            setConfigurationError(error);
            setConfigurationStatus("Error");
            trackEvent({
                eventName: ephemeralEnvironmentConfigurationError,
                "Space Id": anonymize(project.SpaceId),
                "Project Id": anonymize(project.Id),
            });
            return;
        }
        submitConfiguration(provisioningRunbook, deprovisioningRunbook);
    }
    function closeAndReset() {
        if (configurationStatus !== "Success") {
            trackEvent({
                eventName: ephemeralEnvironmentConfigurationCancelled,
                "Page Name": configurationStatus === "Error" ? "Error" : currentPageName,
                "Space Id": anonymize(project.SpaceId),
                "Project Id": anonymize(project.Id),
            });
        }
        closeDialog();
        setConfigurationStatus("Not Started");
        setParentEnvironmentConfig({ id: undefined, name: undefined, action: "Select" });
        setNameTemplateConfig("");
        setProvisioningConfig({ id: undefined, name: undefined, action: onboardingData ? getDefaultRunbookMode(onboardingData) : "Select" });
        setDeprovisioningConfig({ id: undefined, name: undefined, action: onboardingData ? getDefaultRunbookMode(onboardingData) : "Select" });
        setBranchConfig(undefined);
        setConfigurationError(undefined);
        setUseAutomaticBranchName(true);
        setAutomaticEphemeralEnvironmentDeployments(true);
        setCurrentPageName("Parent environment");
    }
    function getDefaultRunbookMode(data: GetEphemeralEnvironmentsOnboardingDataBffResponse): RunbooksInputMode {
        // If you can't configure Runbooks, or can't view Runbooks, we should default to
        // skip. You can't have create permission without view permission so we don't need
        // to specifically handle this case.
        if (!data.Permissions.ConfigureRunbooks || !data.Permissions.ViewRunbooks) {
            return "Skip";
        }
        // If there are no existing runbooks, we don't want to default to select (because
        // there won't be anything to select).
        if (data.Runbooks.length === 0) {
            return data.Permissions.CreateRunbooks ? "Create" : "Skip";
        }
        return "Select";
    }
    return <PagedOnboardingDialog name="config-wizard-dialog" defaultTitle={title} open={isOpen} close={closeAndReset} showPageIndicator={true} pages={pages} hidePreviousButton={configurationStatus != "Not Started"} hideCompleteButton={true}/>;
}
